import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as CryptoJS from 'crypto-js';
import  Moment from 'moment';
import { ToWords } from 'to-words';


import { EventBusService } from '@app-shared/services/event-bus.service';
import { EventData } from '@app-shared/services/event-class';

import { messages } from '@app-shared/constants/error-messages';
import { successMessages } from '@app-shared/constants/success-messages';
import { ApiService } from '@app-shared/services/api.service';
import { ToastrService } from 'ngx-toastr';
import { fields } from '../constants/upload-validation-types';
import { APIRequest } from './req-res.types';
import { URL } from '../constants/service-urls';
import { Router } from '@angular/router';
import { ConfigService } from './config.service';
import { TABLE_COLUMN_PREFERENCES_STORAGE_PREFIX } from '../table/table-column-preferences';

declare const window: any;
declare var $ :any;

interface DialogData{
  title: string,
  message: string
}

@Injectable({
  providedIn: 'root'
})
export class UtilService {
  successNotification(message: string) {
    this.simpleDialog({
      title: "success",
      message: message
    });
  }
  
  encKey: string = '75a7a847e3569085185c6ffdc9c3f3b433378c1d77e2b37a2b11a145f01a';
  clientIPAddr: string = 'x.x.x.x';
  clientGeoLocation: string = '0,0';
  showLoader: boolean = false;
  timeoutId: any;

  constructor(
    private router: Router,
    private api: ApiService,
    private dialog: MatDialog,
    private eventBusService: EventBusService,
    private toastr: ToastrService,
    private config: ConfigService
  ) {}

  get clientBrowser(){
    const userAgent = window.navigator.userAgent.toLowerCase();
    const browser =
    userAgent.indexOf('edge') > -1 ? 'edge'
        : userAgent.indexOf('edg') > -1 ? 'chromium based edge'
        : userAgent.indexOf('opr') > -1 && !!window.opr ? 'opera'
        : userAgent.indexOf('chrome') > -1 && !!window.chrome ? 'chrome'
        : userAgent.indexOf('trident') > -1 ? 'ie'
        : userAgent.indexOf('firefox') > -1 ? 'firefox'
        : userAgent.indexOf('safari') > -1 ? 'safari'
        : 'unknown browser';

    return browser;
  }

  get jwt(){
    return localStorage.getItem('JwtToken');
  }

  getIntlErrorMessage(identifier: string, statusCode: string, statusDesc?: string): string {
    const lang = 'en';
    if (!messages[identifier] || !messages[identifier][statusCode] || !messages[identifier][statusCode][lang]){
      return statusDesc || '';
    }
    return messages[identifier][statusCode][lang];
  }

  errorNotification(errorMessage : string){
    this.simpleDialog({
      title: "error",
      message : errorMessage
    });
  }

  warningNotification(warningMessage : string){
    this.simpleDialog({
      title: "warning",
      message : warningMessage
    });
  }

  getSuccessMessage(identifier: string, statusCode: string, statusDesc: string): string {
    const lang = 'en';
    if (!successMessages[identifier] || !successMessages[identifier][statusCode] || !successMessages[identifier][statusCode][lang]){
      return statusDesc || '';
    }
    return successMessages[identifier][statusCode][lang];
  }

  encrypt(text): string {
    return CryptoJS.AES.encrypt(text, this.encKey).toString();
  }

  decrypt(data: string | null): any {
    if (!data) {
      console.warn('Decrypt called with null or empty data');
      return null;
    }
    try {
      return CryptoJS.AES.decrypt(data, this.encKey).toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Error decrypting data:', error);
      return null;
    }
  }

  simpleDialog(data: DialogData) {
    const options = {
      timeOut: 3000,          // auto close after 5s
      extendedTimeOut: 2000,  // allow hover pause
      disableTimeOut: false,  // ensure auto-close works
      tapToDismiss: true
    };  
    switch (data?.title.toLowerCase()) {
      case "success":
      case "added":
      case "updated":
      case "deleted":      
        this.toastr.success(data?.message,'',options);
        break;
      
      case "error":
     this.toastr.error(data?.message,'',options);
        break;

      case "warning":
   this.toastr.warning(data?.message,'',options);
        break;

      default:
        break;
    }
  }

  async getFileFromUrl(url, name, defaultType = 'image/jpeg'){
    const response = await fetch(url);
    const data = await response.blob();
    return new File([data], name, {
      type: response.headers.get('content-type') || defaultType,
    });
  }

  generatePassword(passwordLength:number) {
    const lowerCharacters = 'abcdefghijklmnopqrstuvwxyz'.split('');
    const upperCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const numbers = '0123456789'.split('');
    const symbols = '!@?#'.split('');

    
    const allCharacters = [...lowerCharacters, ...upperCharacters, ...numbers, ...symbols];
    const getRandom = (array: string[]) => array[Math.floor(Math.random() * array.length)];

    let finalCharacters = [
    getRandom(lowerCharacters),
    getRandom(upperCharacters),
    getRandom(numbers),
    getRandom(symbols)
    ];

    for (let i = finalCharacters.length; i < passwordLength; i++) {
      finalCharacters.push(getRandom(allCharacters));
    }

    return finalCharacters.sort(() => 0.5 - Math.random()).join('');
  }

  cancelTimeout() {
    clearTimeout(this.timeoutId);
  }

  async refreshAccessToken(refreshToken: string): Promise<void> {
    try {
      const response = await fetch(this.config.apiBaseURL+'/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add Authorization header if needed:
          // 'Authorization': `Bearer ${refreshToken}`,
        },
        body: JSON.stringify({
          requestHead: {
            appName: 'Certiplate UI',
            platform: 'web',
          },
          requestBody: {
            refreshToken: refreshToken
          },
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      const newToken = data?.responseBody?.jwtToken;
      if (!newToken) {
        throw new Error('No token received');
      }
  
      localStorage.setItem('JwtToken', newToken);
      this.setTokenExpiryPopup(newToken, refreshToken); // re-init with new token
    } catch (err) {
      console.error('Token refresh failed:', err);
      this.handleSessionExpiry();
    }
  }
  

  setTokenExpiryPopup(token: string, refreshToken: string) {
    if (typeof this.timeoutId === 'number') {
      this.cancelTimeout();
    }
    const jwtToken = JSON.parse(atob(token.split('.')[1]));
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now();
  
    // Refresh 30 seconds before actual expiry
    const refreshTimeout = timeout - 5 * 60 * 1000;
  
    if (refreshTimeout <= 0) {
      this.handleSessionExpiry();
      return;
    }
  
    this.timeoutId = setTimeout(async () => {
      this.refreshAccessToken(refreshToken);
    }, refreshTimeout);
  }
  
  private handleSessionExpiry() {
    this.clearStoragePreservingTableColumnPreferences();
    this.router.navigate(['/account/login']);
    const errorMessage = this.getIntlErrorMessage('SESSION_EXPIRED', '9999');
    this.simpleDialog({
      title: 'Error',
      message: errorMessage,
    });
    this.timeoutId = undefined;
    $('.modal').modal('hide');
  }

  clearStoragePreservingTableColumnPreferences() {
    const preservedEntries: Array<[string, string]> = [];

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(TABLE_COLUMN_PREFERENCES_STORAGE_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          preservedEntries.push([key, value]);
        }
      }
    });

    localStorage.clear();

    preservedEntries.forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  }

  refreshToken(data: any) {
    const postData: APIRequest = {
        
      requestBody: {
        ...data,
      },
    };
    return this.api.post(URL.ACCOUNT.REFRESH_TOKEN, postData);
  }
  

  getFormatedDate(inputDate: any, dateFormat: string) {
    const utcDate = new Date(inputDate);
    return Moment(utcDate).format(dateFormat);
  }

  getFeatureAccess(menus: any, menuName: string, pageName: string): string[] {
    const access: string[] = [];

    // Recursive function to traverse pages and extract features
    const extractFeatures = (pages: any[]) => {
      pages.forEach((page) => {
        if (page.features && page.features.length) {
          const features = page.features.map((feature: any) => feature.feature_name);
          access.push(...features);
        }
        if (page.pages && page.pages.length) {
          extractFeatures(page.pages); // Recursively process nested pages
        }
      });
    };

    const menu = menus.find((ele: any) => ele.menu_name.toLowerCase() === menuName.toLowerCase());
    if (menu && menu.pages && menu.pages.length) {
      const page = menu.pages.find((ele: any) => ele.menu_name.toLowerCase() === pageName.toLowerCase());
      if (page) {
        extractFeatures([page]); // Start extracting features from the target page
      }
    }

    return access;
  }

  getCourseLabelName(labelId: number) {
    const courseFrameworkLabels = JSON.parse(this.decrypt(localStorage.getItem('course_framework_labels')));
    if (!courseFrameworkLabels || !courseFrameworkLabels.length) {
      return '';
    }
    const filteredData = courseFrameworkLabels.find((ele: any) => ele.label_type_id === labelId);
    if (filteredData && filteredData.label_name) {
      return filteredData.label_name;
    }
    return '';
  }

  getPluralString(name: string) {
    const specialPlurals = ['Class', 'Batch', 'Branch'];
    const [str1, str2] = name.split('/');
    let resultStr: string;
    if (str1 && specialPlurals.includes(str1)) {
      resultStr = `${str1}es`;
    } else {
      resultStr = `${str1}s`;
    }
    if (str2 && specialPlurals.includes(str2)) {
      resultStr += `/${str2}es`;
    } else if(str2) {
      resultStr += `/${str2}s`;
    }
    return resultStr;
  }

  getPluralCourseLabelName(labelId: number) {
    const courseFrameworkLabels = JSON.parse(this.decrypt(localStorage.getItem('course_framework_labels')));
    if (!courseFrameworkLabels || !courseFrameworkLabels.length) {
      return '';
    }
    const filteredData = courseFrameworkLabels.find((ele: any) => ele.label_type_id === labelId);
    if (filteredData && filteredData.label_name) {
      return this.getPluralString(filteredData.label_name);
    }
    return '';
  }

  

  mceOptionConfig = {
    height: 200,
    menubar: false,
    plugins: 'print preview paste importcss searchreplace autolink autosave \
    save directionality code visualblocks visualchars fullscreen image link \
    tiny_mce_wiris \
    media template codesample table charmap hr pagebreak nonbreaking anchor toc insertdatetime \
    advlist lists wordcount imagetools textpattern \
    noneditable help charmap quickbars emoticons',

    external_plugins:  {tiny_mce_wiris: 'https://www.wiris.net/demo/plugins/tiny_mce/plugin.js' },

    toolbar:
      'undo redo | preview | formatselect | bold italic backcolor | \
      tiny_mce_wiris_formulaEditor | tiny_mce_wiris_formulaEditorChemistry|\
      alignleft aligncenter alignright alignjustify | \
      bullist numlist outdent indent | removeformat | help | \
      link image | code | forecolor backcolor removeformat | \
      pagebreak | charmap emoticons | fullscreen  preview save print | \
      insertfile image media template link anchor codesample | ltr rtl'
  }

  mceNonEditorConfig = {
    height: 400,
    menubar: false,
    toolbar: false,
    selector: 'textarea',
    plugins: 'noneditable autoresize',
    noneditable_noneditable_class: 'mceNonEditable',
    readonly: true,
    setup: function(editor) {
      editor.on('keydown', function (event) {
        if (event.keyCode === 8 || event.keyCode === 46)  {
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
      });
    }
  }

  //Function to get the course menu heading wrt labels
  getCourseHeadingName(id: any) {
    let heading: any;
    switch(parseInt(id, 10)) {
      case 1:
        heading = 'Qualification Pack';
        break;
      case 2:
        heading = 'Course';
        break;
      case 3:
        heading = 'Subject';
        break;
      case 4:
        heading = 'Subject';
        break;
      case 5:
        heading = 'Course';
        break;
      case 6:
        heading = 'Function';
        break;
      default:
        heading = 'Course';
    }
    return heading;
  }

  getModuleHeadingName(id: any) {
    let heading: any;
    switch(parseInt(id, 10)) {
      case 1:
        heading = 'NOS';
        break;
      case 2:
        heading = 'Module';
        break;
      case 3:
        heading = 'Chapter';
        break;
      case 4:
        heading = 'Chapter';
        break;
      case 5:
        heading = 'Module';
        break;
      default:
        heading = 'Module';
    }
    return heading;
  }

  getTopicHeadingName(id: any) {
    let heading: any;
    switch(parseInt(id, 10)) {
      case 1:
        heading = 'PC';
        break;
      case 2:
        heading = 'Topic';
        break;
      case 3:
        heading = 'Topic';
        break;
      case 4:
        heading = 'Topic';
        break;
      case 5:
        heading = 'Topic';
        break;
      default:
        heading = 'Topic';
    }
    return heading;
  }

  getFieldList(type: string) {
    return fields[type.toUpperCase()]
  }

  range = (start: number, end: number, step: number) => {
    return Array.from(Array.from(Array(Math.ceil((end-start)/step)).keys()), x => start+ x*step);
  }

  getFormattedNotifications(list: any[]) {
    if (list.length > 0) {
      const arr = list?.slice().sort(function (a: any, b: any): any {
        return +new Date(b?.created_datetime) - +new Date(a?.created_datetime)
      })

      let currentDate = Moment(new Date()).format('DD-MM-YYYY')
      let yesterdayDate = Moment().subtract(1, 'day').format('DD-MM-YYYY')

      const groups = arr.reduce((groups: any, listItem: any) => {
        let date = Moment(listItem.created_datetime.split('T')[0]).format('DD-MM-YYYY')
        if (date === currentDate) date = 'TODAY'
        else if (Moment(listItem.created_datetime).format('DD-MM-YYYY') === yesterdayDate)
          date = 'YESTERDAY'
        else date = 'OLDER'

        if (!groups[date]) {
          groups[date] = []
        }
        groups[date].push(listItem)
        return groups
      }, {})

      return groups
    }
    return []
  }

  openNotificationDialog(title: string, message: string, customClass: string) {
    this.toastr.show(title, message, {
      positionClass: 'toast-bottom-right',
      toastClass: customClass,
      tapToDismiss: true,
      timeOut: 5000
    });
  }

  getApiUrl() {
    const apiUrls = {
      local: 'http://localhost:8080',
      'app-dev': 'https://api-dev.certiplate.com',
      'app2-dev': 'https://api-dev.certiplate.com',
      'app-qa': 'https://api-qa.certiplate.com',
      'app2-qa': 'https://api-qa.certiplate.com',
      'app-uat': 'https://api-uat.certiplate.com',
      prod: 'https://api-prod.certiplate.com'  
    }
    let env: any;
    if (window.location.hostname === 'localhost') {
      env = 'local';
    } else {
      const environment = window.location.host.replace('.certiplate.com', '');
      env = ['app-dev', 'app2-dev', 'app-qa', 'app2-qa', 'app-uat'].includes(environment) ? environment : 'prod';
    }
    return apiUrls[env];
  }

  isJson(str: any) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
  }

  getToWordsConfig() {
    return new ToWords({
      localeCode: 'en-IN',
      converterOptions: {
        currency: true,
        ignoreDecimal: false,
        ignoreZeroCurrency: false,
        doNotAddOnly: false,
        currencyOptions: { // can be used to override defaults for the selected locale
          name: 'Rupee',
          plural: 'Rupees',
          symbol: '₹',
          fractionalUnit: {
            name: 'Paisa',
            plural: 'Paise',
            symbol: '',
          },
        }
      }
    });
  }

  getBlankIndicesStartEndData(formValue) {
    const { actualText_1, possibleAnswers } = formValue;
    const blankIndicesStartEndData = [];
    const wordBlankArray = actualText_1.match(/[^[\]]+(?=])/g);
    if (wordBlankArray && wordBlankArray.length) {
      wordBlankArray.forEach((ele: any, index: any) => {
        const blankWord = `[${ele}]`;
        const startIndex = actualText_1.indexOf(blankWord);
        const endIndex = startIndex + blankWord.length - 1;
        const item = {
          start_index: startIndex,
          end_index: endIndex,
          blank_text: blankWord,
          ans: possibleAnswers[index]?.answers,
        };
        blankIndicesStartEndData.push(item);
      });
    }
    return JSON.stringify(blankIndicesStartEndData);
  }

  getBlankTextAnswerKeyData(formValue) {
  const { possibleAnswers } = formValue;
  const blankTextAnswerKeyData = [];
  if (possibleAnswers && possibleAnswers.length) {
    possibleAnswers.forEach((ele: any, index: number) => {
      // Process answer: newlines → <br>, but KEEP spaces as-is
      let answerValue = ele.answers
        .replace(/\n/g, '<br>');
      
      // Escape double quotes in the answer value for hstore format
      answerValue = answerValue.replace(/"/g, '\\"');
      
      // Format as proper hstore: "key"=>"value"
      const eleStr = `"${index + 1}"=>"${answerValue}"`;
      blankTextAnswerKeyData.push(eleStr);
    });
  }
  return blankTextAnswerKeyData.join(',');
}


getMatchingJson(formValue) {
  const { primaryList, secondaryList } = formValue;
  const matchResult = [];
  let secondaryMapping: Record<number, number> = {}; // store secondary sequence mapping
  // Process secondaryList first to get mapping
  if (secondaryList && secondaryList.length) {
    secondaryList.forEach((ele: any) => {
      if (ele.lists && ele.lists.length) {
        ele.lists.forEach((list: any, index: number) => {
          // Map secondary index => correctSequence
          secondaryMapping[index + 1] = parseInt(list.correctSequence);
        });
      }
    });
  }
    if (primaryList && primaryList.length) {
      const listValues = [];
      const attachments = [];
      const correctSequence = [];
      primaryList.forEach((ele: any, index: number) => {
        if (ele.value) {
          listValues.push(`${index + 1}=>${ele.value}`);
        }
        if (ele.attachment) {
          attachments.push(`${index + 1}=>${ele.attachment}`);
        }
      // Use reverse mapping of secondary list
      // Find which primary index corresponds to which secondary sequence
      const secondaryIndex = Object.keys(secondaryMapping).find(
        (key) => secondaryMapping[parseInt(key)] === index + 1
      );
        correctSequence.push(`${index + 1}=>${secondaryIndex}`);
      });
      matchResult.push({
        matchId: primaryList[0]?.matchId || 0,
        listOrderId: 1,
        isPrimaryList: true,
        listValues: listValues.join(','),
        listAttachments: attachments.join(','),
        correctSequence: correctSequence.join(','),
        isDeleted: false,
      });
    }
    if (secondaryList && secondaryList.length) {
      secondaryList.forEach((ele: any) => {
        if (ele.lists && ele.lists.length) {
          const listValues = [];
          const attachments = [];
          const correctSequence = [];
          let matchId;
          ele.lists.forEach((list: any, index: number) => {
            if (list.value) {
              listValues.push(`${index + 1}=>${list.value}`);
            }
            if (list.attachment) {
              attachments.push(`${index + 1}=>${list.attachment}`);
            }
            correctSequence.push(`${index + 1}=>${list.correctSequence}`);
            matchId = list.matchId;
          });
          matchResult.push({
            matchId: matchId || 0,
            listOrderId: 2,
            isPrimaryList: false,
            listValues: listValues.join(','),
            listAttachments: attachments.join(','),
            correctSequence: correctSequence.join(','),
            isDeleted: false,
          });
        }
      });
    }
    return matchResult;
  }

  capitalizeFirstLetter(str) {
    if (str.length === 0) {
      return str;
    }

    return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  }

  formatAndSetDateTime(localDate, localTime) {
    const dateParts = localDate.split('/');
    const timeParts = localTime.split(':');
  
    // Create a Date object with the parsed values
    const combinedDate = new Date(
      dateParts[2], // Year
      dateParts[1] - 1, // Month (subtract 1 because months are 0-based in JavaScript)
      dateParts[0], // Day
      timeParts[0], // Hours
      timeParts[1], // Minutes
      timeParts[2] // Seconds
    );
  
    // Format the combinedDate as yyyy-MM-ddThh:mm:ss
    return combinedDate.toISOString().slice(0, 16);
  }
}
